import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  PatchPerspectiveBodyDto,
  PostPerspectiveBodyDto,
  groupKitExpertPerspectiveControllerCreate,
  groupKitExpertPerspectiveControllerDelete,
  groupKitExpertPerspectiveControllerGetMany,
  groupKitExpertPerspectiveControllerGetOne,
  groupKitExpertPerspectiveControllerUpdate,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

/**
 * Hook to manage skills data for a specific expert.
 * Provides functionality to load all skills for an expert and create new ones.
 */
export const usePerspectivesData = (
  groupUuid: string | null,
  kitUuid: string | null,
  expertUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const {
    data: perspectives,
    isLoading,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid && !!expertUuid,
    initialData: [],
    queryKey: queryKeys.perspectives(groupUuid!, kitUuid!, expertUuid!),
    queryFn: () => {
      return groupKitExpertPerspectiveControllerGetMany({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          expertUuid: expertUuid!,
        },
      }).then((res) => res.data?.data ?? []);
    },
  });

  const { mutateAsync: createPerspective, isPending: isCreatingPerspective } =
    useMutation({
      mutationFn: async (body: PostPerspectiveBodyDto) => {
        if (!groupUuid || !kitUuid || !expertUuid) {
          throw new Error(
            "Group UUID, Kit UUID, and Expert UUID are required for creating perspectives",
          );
        }

        const perspective = await groupKitExpertPerspectiveControllerCreate({
          path: {
            groupUuid,
            kitUuid,
            expertUuid,
          },
          body,
        });

        return perspective.data;
      },
      onSuccess: () => {
        // Invalidate the skills query to refetch the updated list
        if (groupUuid && kitUuid && expertUuid) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.perspectives(groupUuid, kitUuid, expertUuid),
          });
        }
      },
    });

  const { mutateAsync: deletePerspective, isPending: isDeletingPerspective } =
    useMutation({
      mutationFn: async (perspectiveUuid: string) => {
        if (!groupUuid || !kitUuid || !expertUuid) {
          throw new Error(
            "Group UUID, Kit UUID, and Expert UUID are required for deleting perspectives",
          );
        }

        const result = await groupKitExpertPerspectiveControllerDelete({
          path: {
            groupUuid,
            kitUuid,
            expertUuid,
            perspectiveUuid,
          },
        });

        return result;
      },
      onSuccess: () => {
        // Invalidate the perspectives query to refetch the updated list
        if (groupUuid && kitUuid && expertUuid) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.perspectives(groupUuid, kitUuid, expertUuid),
          });
        }
      },
    });

  return {
    perspectives,
    isLoading,
    error,
    createPerspective,
    isCreatingPerspective,
    deletePerspective,
    isDeletingPerspective,
  };
};

/**
 * Hook to manage individual skill data.
 * Provides functionality to load and update a specific skill.
 */
export const usePerspectiveData = (
  groupUuid: string | null,
  kitUuid: string | null,
  expertUuid: string | null,
  perspectiveUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const {
    data: perspective,
    isLoading,
    error,
  } = useQuery({
    enabled:
      useIsApiReady() &&
      !!groupUuid &&
      !!kitUuid &&
      !!expertUuid &&
      !!perspectiveUuid,
    queryKey: ["skill", perspectiveUuid],
    queryFn: () => {
      return groupKitExpertPerspectiveControllerGetOne({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          expertUuid: expertUuid!,
          perspectiveUuid: perspectiveUuid!,
        },
      }).then((res) => res.data ?? null);
    },
  });

  const { mutateAsync: updatePerspective, isPending: isUpdatingPerspective } =
    useMutation({
      mutationFn: async (vars: PatchPerspectiveBodyDto) => {
        if (!groupUuid || !kitUuid || !expertUuid || !perspectiveUuid) {
          throw new Error("All UUIDs are required for updating skills");
        }

        const updatedSkill = await groupKitExpertPerspectiveControllerUpdate({
          path: {
            groupUuid,
            kitUuid,
            expertUuid,
            perspectiveUuid,
          },
          body: vars,
        });

        return updatedSkill;
      },
      onSuccess: () => {
        // Invalidate both the individual skill query and the skills list
        if (groupUuid && kitUuid && expertUuid && perspectiveUuid) {
          queryClient.invalidateQueries({
            queryKey: ["skill", perspectiveUuid],
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.perspectives(groupUuid, kitUuid, expertUuid),
          });
        }
      },
    });

  return {
    perspective,
    isLoading,
    error,
    updatePerspective,
    isUpdatingPerspective,
  };
};
